import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Building2, FileSpreadsheet, Landmark, Repeat, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const TOTAL = 90000;
const MODE_DEFAULTS = {
  banca: { tan: 6.9, anticipo: 25 },
  leasing: { tan: 5.5, anticipo: 45 },
  noleggio: { tan: 6.5, anticipo: 0 },
};
const DURATIONS = [12, 24, 36, 48, 60, 72, 84];

const eur = (n) => "€ " + Math.round(n).toLocaleString("it-IT");

const Range = ({ label, value, min, max, step, onChange, prefix = "", suffix = "", testid }) => {
  const pct = Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
  return (
    <div>
      <div className="flex items-center justify-between text-sm font-semibold mb-2">
        <span className="text-ink">{label}</span>
        <span className="text-tricolor-green">{prefix}{value}{suffix}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="sim-range"
        style={{ background: `linear-gradient(to right, #E60000 ${pct}%, #e5e5e5 ${pct}%)` }}
        data-lenis-prevent
        data-testid={testid}
      />
    </div>
  );
};

export const Simulator = ({ onSummary }) => {
  const { t } = useLang();
  const s = t.simulator;
  const [mode, setMode] = useState("noleggio");
  const [anticipoPct, setAnticipoPct] = useState(0);
  const [durata, setDurata] = useState(60);
  const [tan, setTan] = useState(6.5);
  const [ticket, setTicket] = useState(12);
  const [scontrini, setScontrini] = useState(60);
  const [margin, setMargin] = useState(35);

  const setModeAndDefaults = (key) => {
    setMode(key);
    setTan(MODE_DEFAULTS[key].tan);
    setAnticipoPct(MODE_DEFAULTS[key].anticipo);
  };

  const c = useMemo(() => {
    const anticipo = TOTAL * (anticipoPct / 100);
    const financed = TOTAL - anticipo;
    const r = tan / 100 / 12;
    const n = durata;
    const rata = financed > 0 && r > 0 ? (financed * r) / (1 - Math.pow(1 + r, -n)) : financed / n;
    const totalPaid = rata * n;
    const totInteressi = totalPaid - financed;
    const costoTotale = anticipo + totalPaid;
    const dailyRate = rata / 30;
    const beScontrini = Math.max(1, Math.ceil(dailyRate / ticket));
    const safety = scontrini - beScontrini;
    const ricavoMensile = ticket * scontrini * 30;
    const utileOperativo = (ricavoMensile * margin) / 100;
    const utileNetto = utileOperativo - rata;
    const payback = utileOperativo > 0 ? Math.round(TOTAL / utileOperativo) : 0;

    let residual = financed;
    const amort = [];
    for (let i = 1; i <= 6; i++) {
      const interest = residual * r;
      const capital = rata - interest;
      residual -= capital;
      amort.push({ i, rata, interest, capital, residual: Math.max(0, residual) });
    }
    return { anticipo, financed, rata, totInteressi, costoTotale, dailyRate, beScontrini, safety, ricavoMensile, utileNetto, payback, amort };
  }, [anticipoPct, durata, tan, ticket, scontrini, margin]);

  const modeIcons = { banca: Landmark, leasing: FileSpreadsheet, noleggio: Repeat };
  const currentMode = s.modes.find((m) => m.key === mode);

  useEffect(() => {
    if (!onSummary) return;
    onSummary({
      modalita: currentMode?.label || mode,
      anticipo: `${anticipoPct}% (${eur(c.anticipo)})`,
      durata: `${durata} ${s.months}`,
      tan: `${tan}%`,
      rata: `${eur(c.rata)}/mese`,
      ticket: `€ ${ticket}`,
      scontriniGiorno: scontrini,
      payback: `${c.payback} ${s.months}`,
      costoTotale: eur(c.costoTotale),
    });
  }, [mode, anticipoPct, durata, tan, ticket, scontrini, margin, c, onSummary, currentMode, s.months]);

  return (
    <div className="space-y-6">
      {/* Breakdown */}
      <div className="card p-7 md:p-8" data-testid="sim-breakdown">
        <h3 className="font-display font-bold text-xl text-ink mb-5">{s.breakdownTitle}</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {s.breakdown.map((b, i) => (
            <div key={i} className="flex items-center justify-between rounded-xl bg-surface px-4 py-3 border-l-2 border-tricolor-green">
              <span className="text-sm text-ink/70">{b.label}</span>
              <span className="font-semibold text-ink">{b.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Mode */}
      <div className="card p-7 md:p-8">
        <h3 className="font-display font-bold text-xl text-ink mb-5">{s.modeTitle}</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          {s.modes.map((m) => {
            const Icon = modeIcons[m.key];
            const active = mode === m.key;
            return (
              <button key={m.key} onClick={() => setModeAndDefaults(m.key)}
                className={`rounded-2xl border p-5 text-left transition-colors duration-200 ${active ? "border-tricolor-green bg-tricolor-green/5" : "border-line hover:border-ink/30"}`}
                data-testid={`sim-mode-${m.key}`}>
                <Icon size={22} className={active ? "text-tricolor-green" : "text-ink"} />
                <div className="mt-3 font-semibold text-ink">{m.label}</div>
              </button>
            );
          })}
        </div>
        <p className="mt-4 text-sm text-muted leading-relaxed">{currentMode?.desc}</p>
      </div>

      {/* Params */}
      <div className="card p-7 md:p-8">
        <h3 className="font-display font-bold text-xl text-ink mb-6">{s.paramsTitle}</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <Range label={s.anticipoIniziale} value={anticipoPct} min={0} max={50} step={1} onChange={setAnticipoPct} suffix="%" testid="sim-anticipo" />
          <div>
            <label className="block text-sm font-semibold text-ink mb-2">{s.durataLabel}</label>
            <select value={durata} onChange={(e) => setDurata(Number(e.target.value))} className="w-full rounded-xl border border-line bg-white px-4 py-2.5 focus:outline-none focus:border-tricolor-green" data-testid="sim-durata">
              {DURATIONS.map((d) => <option key={d} value={d}>{d} {s.months}</option>)}
            </select>
          </div>
          <div>
            <Range label={s.tanLabel} value={tan} min={2} max={12} step={0.1} onChange={setTan} suffix="%" testid="sim-tan" />
          </div>
        </div>
      </div>

      {/* Rate */}
      <div className="rounded-3xl bg-ink text-white p-7 md:p-8" data-testid="sim-rate">
        <span className="eyebrow">{s.rateTitle}</span>
        <div className="mt-3 flex items-end gap-3">
          <span className="font-display font-extrabold text-5xl md:text-6xl text-tricolor-green" data-testid="sim-rate-value">{eur(c.rata)}</span>
          <span className="text-white/60 mb-2">{s.rateSuffix} {durata} {s.months}</span>
        </div>
        <div className="mt-6 grid grid-cols-3 gap-px bg-white/10 rounded-xl overflow-hidden">
          <div className="bg-ink p-4"><div className="text-sm font-semibold">{eur(c.anticipo)}</div><div className="text-[10px] uppercase tracking-wide text-white/40">{s.anticipoIniziale}</div></div>
          <div className="bg-ink p-4"><div className="text-sm font-semibold">{eur(c.totInteressi)}</div><div className="text-[10px] uppercase tracking-wide text-white/40">{s.totInteressi}</div></div>
          <div className="bg-ink p-4"><div className="text-sm font-semibold">{eur(c.costoTotale)}</div><div className="text-[10px] uppercase tracking-wide text-white/40">{s.costoTotale}</div></div>
        </div>
      </div>

      {/* Break-even */}
      <div className="card p-7 md:p-8" data-testid="sim-breakeven">
        <h3 className="font-display font-bold text-xl text-ink">{s.beTitle}</h3>
        <p className="mt-2 text-sm text-muted leading-relaxed">{s.beIntro}</p>
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div>
            <Range label={s.ticketLabel} value={ticket} min={6} max={25} step={0.5} onChange={setTicket} prefix="€ " testid="sim-ticket" />
          </div>
          <div>
            <Range label={s.scontriniLabel} value={scontrini} min={20} max={300} step={5} onChange={setScontrini} suffix="/gg" testid="sim-scontrini" />
          </div>
        </div>
        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          <div className="rounded-2xl bg-tricolor-green/5 border border-tricolor-green/20 p-5">
            <div className="font-display font-extrabold text-3xl text-tricolor-green" data-testid="sim-be-value">{c.beScontrini}</div>
            <div className="text-xs text-muted mt-1">{s.beCover}</div>
          </div>
          <div className="rounded-2xl bg-surface p-5"><div className="font-semibold text-ink text-lg">{eur(c.dailyRate)}</div><div className="text-xs text-muted mt-1">{s.beDailyCost}</div></div>
          <div className="rounded-2xl bg-surface p-5"><div className={`font-semibold text-lg ${c.safety >= 0 ? "text-emerald-600" : "text-tricolor-red"}`}>{c.safety >= 0 ? "+" : ""}{c.safety}/gg</div><div className="text-xs text-muted mt-1">{s.beSafety}</div></div>
        </div>
      </div>

      {/* Payback */}
      <div className="card p-7 md:p-8" data-testid="sim-payback">
        <h3 className="font-display font-bold text-xl text-ink">{s.paybackTitle}</h3>
        <div className="mt-5 max-w-md">
          <Range label={s.marginLabel} value={margin} min={15} max={55} step={1} onChange={setMargin} suffix="%" testid="sim-margin" />
        </div>
        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          <div className="rounded-2xl bg-surface p-5"><div className="font-semibold text-ink text-xl">{eur(c.ricavoMensile)}</div><div className="text-xs text-muted mt-1">{s.ricavoMensile}</div></div>
          <div className="rounded-2xl bg-surface p-5"><div className={`font-semibold text-xl ${c.utileNetto >= 0 ? "text-emerald-600" : "text-tricolor-red"}`}>{eur(c.utileNetto)}</div><div className="text-xs text-muted mt-1">{s.utileNetto} · {s.utileSuffix}</div></div>
          <div className="rounded-2xl bg-ink text-white p-5"><div className="font-display font-extrabold text-3xl text-tricolor-green" data-testid="sim-payback-value">{c.payback} {s.months}</div><div className="text-xs text-white/50 mt-1">{s.paybackLabel} · ≈ {(c.payback / 12).toFixed(1)} {s.paybackYears}</div></div>
        </div>
      </div>

      {/* Amortization */}
      <div className="card p-7 md:p-8 overflow-x-auto" data-testid="sim-amort">
        <h3 className="font-display font-bold text-xl text-ink">{s.amortTitle}</h3>
        <p className="mt-2 text-sm text-muted">{s.amortIntro}</p>
        <table className="mt-5 w-full text-sm min-w-[520px]">
          <thead>
            <tr className="text-left text-muted border-b border-line">
              {s.amortCols.map((col) => <th key={col} className="py-2 font-semibold">{col}</th>)}
            </tr>
          </thead>
          <tbody>
            {c.amort.map((row) => (
              <tr key={row.i} className="border-b border-line/60">
                <td className="py-2.5 font-semibold text-ink">#{row.i}</td>
                <td className="py-2.5">{eur(row.rata)}</td>
                <td className="py-2.5 text-tricolor-red">{eur(row.interest)}</td>
                <td className="py-2.5 text-emerald-600">{eur(row.capital)}</td>
                <td className="py-2.5 text-ink">{eur(row.residual)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted leading-relaxed">{s.disclaimer}</p>
    </div>
  );
};
